import { colors, sizes } from '@lmc/common/constants'
import { PageHeader } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import { getNominative } from '../../../../common/utils'
import {
  AccessRestriction,
  ListItem,
  SectionHeader
} from '../../../../components/generic'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import DataSource from '../../components/DataSource'
import FluidsDisplay from './components/FluidsDisplay'
import { getChartIconMap } from './utils'

const isDesktop = isBrowser || isTablet

export class ResidentChartsDashboardScreen extends Component {
  formatChartDescription(chart, item) {
    let desc = ''
    if (item.formatValue) {
      if (chart.value) {
        desc = item.formatValue(chart.value)
      }
    } else {
      if (item.unit) {
        desc = `${chart.value?.[chart.key] || 0}${item.unit}`
      }
      if (chart.description && chart.description.length > 200) {
        desc += `${chart.description.slice(0, 200)}...`
      } else if (chart.category === 'Clinical Care') {
        desc += `${chart.value.fluids_in} ml last 24h`
      } else if (chart.description) {
        desc = chart.description
      }
    }

    return desc
  }

  renderSection({ title, charts, reportingTypes }) {
    const {
      router: { params },
      testId
    } = this.props
    if (!charts || !charts.length || !params.id) {
      return null
    }
    const items = _.keyBy(reportingTypes, 'key')
    const sortedCharts = _.sortBy(
      charts,
      (chart) => `${chart.category}${chart.key}`
    )
    return (
      <div key={title} style={styles.sectionContainer}>
        <div style={styles.sectionCard}>
          <SectionHeader title={title} />
          <div style={styles.sectionCardBody}>
            {sortedCharts.map((chart) => {
              const item = items[chart.key]
              const resident = params.id
              let { key } = chart
              if (key === 'fluids_in') {
                key = 'fluids'
              }
              if (!item) {
                return null
              }
              return (
                <ListItem
                  key={key}
                  label={`${item.label}`}
                  borderRadius={300}
                  iconColor={chart.color}
                  icon={chart.icon}
                  text={this.formatChartDescription(chart, item)}
                  subLabel={
                    chart.timeLogged &&
                    dayjs(chart.timeLogged).format('DD/MM/YY [\u2022] HH:mm')
                  }
                  to={`/residents/${resident}/charts/${key}`}
                  style={styles.recordCardListItem}
                  data-testid={
                    testId ? `${testId}-chart-item-${key}` : `chart-item-${key}`
                  }
                >
                  {key === 'fluids' && (
                    <FluidsDisplay
                      residentId={resident}
                      flag="fluid_tracking_intake_targets"
                    />
                  )}
                </ListItem>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  renderNoData(noDataTypes) {
    const {
      logCategories,
      logCategoryItems,
      router: { params },
      preferredPeopleNominative,
      testId
    } = this.props
    if (!noDataTypes || !noDataTypes.length || !params.id) {
      return null
    }
    const sortedItems = _.sortBy(noDataTypes, 'label')
    const supportLine = (
      <span>
        {`${getNominative(
          preferredPeopleNominative,
          'Resident'
        )} charts are populated by logs made via the Carer App.`}
        <AccessRestriction>
          <a
            href={SUPPORT_ARTICLE}
            rel="noreferrer"
            style={{ paddingLeft: 5 }}
            target="_blank"
          >
            Find out more
          </a>
        </AccessRestriction>
      </span>
    )
    const chartIconMap = getChartIconMap(
      logCategories ?? [],
      logCategoryItems ?? []
    )

    return (
      <div style={styles.sectionContainer}>
        <div style={styles.groupTitle}>No Recorded Data</div>
        <div style={styles.sectionCard}>
          <SectionHeader
            title="Select a title below to view a sample chart"
            subtitle={supportLine}
          />
          <div style={styles.sectionCardBody}>
            {sortedItems.map((row) => (
              <ListItem
                key={row.key}
                label={`${row.label}`}
                borderRadius={300}
                iconColor={chartIconMap[row.key]?.color}
                icon={chartIconMap[row.key]?.icon}
                noIcon={!chartIconMap[row.key]}
                to={`/residents/${params.id}/charts/${row.key}`}
                style={styles.recordCardListItem}
                data-testid={
                  testId
                    ? `${testId}-no-data-link-${row.key}`
                    : `no-data-link-${row.key}`
                }
              ></ListItem>
            ))}
          </div>
        </div>
      </div>
    )
  }

  getReportingTypes = () =>
    [
      { label: 'Body Maps', key: 'body_maps', disabled: !this.props.bodyMaps },
      {
        label: 'Fluids',
        key: 'fluids',
        unit: ' ml last 24h',
        hasMock: true
      },
      {
        label: 'Food',
        key: 'meal',
        unit: ' portions last 24h',
        hasMock: true,
        formatValue: ({ meal }) => {
          // if (value < 1) return 'Less than 1 portion in 24h'
          if (meal === 1) {
            return '1 portion in 24h'
          }
          return `${meal} portions in 24h`
        }
      },
      { label: 'Health Visit', key: 'health_visit' },
      { label: 'Incident', key: 'incident' },
      {
        label: 'PainChek',
        key: 'painchek'
        // formatValue: (val) => {
        //   return val.description
        // },
      },
      { label: 'Behaviour', key: 'behaviour' },
      { label: 'Activities & Social', key: 'activity_social' },
      {
        label: 'Blood Pressure',
        formatValue: ({ blood_pressure_lower, blood_pressure_upper }) =>
          `${blood_pressure_upper}/${blood_pressure_lower} mmHg`,
        key: 'blood_pressure',
        unit: ' mmHg',
        hasMock: true
      },
      {
        label: 'Blood Glucose',
        key: 'blood_glucose',
        unit: ' mmol/L',
        formatValue: ({ blood_glucose_before, blood_glucose_after }) => {
          if (blood_glucose_before) {
            return `${blood_glucose_before} mmol/L before eating`
          }
          return `${blood_glucose_after} mmol/L after eating`
        },
        hasMock: true
      },
      {
        label: 'Respiratory Rate',
        key: 'respiratory_rate',
        unit: ' breaths / min'
      },
      {
        label: 'Blood Oxygen',
        key: 'blood_oxygen',
        unit: '% SpO2',
        hasMock: true
      },
      { label: 'Heart Rate', key: 'heart_rate', unit: 'bpm', hasMock: true },
      { label: 'MUST', key: 'must', hasMock: true },
      { label: 'Temperature', key: 'temperature', unit: '°C', hasMock: true },
      {
        label: 'Re-positioning',
        key: 'turns',
        unit: ' in last 24h',
        hasMock: true
      },
      { label: 'Wash', key: 'wash', hasMock: false },
      { label: 'Waterlow', key: 'waterlow', hasMock: true },
      { label: 'Weight', key: 'weight', unit: ' kg', hasMock: true },
      {
        label: 'Stool',
        key: 'stool',
        hasMock: true,
        formatValue: ({ stool }) =>
          !_.isNumber(stool) ? stool || 'Normal' : `Type ${stool}`
      },
      {
        label: 'Mood',
        key: 'mood',
        hasMock: true,
        formatValue: ({ mood }) => {
          const moodMap = {
            1: 'Lowest',
            2: 'Low',
            3: 'Neutral',
            4: 'Good',
            5: 'Best'
          }
          return moodMap[mood] || mood
        }
      },
      {
        label: 'Mobility',
        key: 'mobility',
        formatValue: ({ mobility }) => {
          const mapping = {
            1: 'Very Poor',
            2: 'Poor',
            3: 'Average',
            4: 'Good',
            5: 'Very Good'
          }
          return mapping[mobility] || mobility
        }
      },
      {
        label: 'Seizure',
        key: 'seizure'
      }
    ].filter((row) => !row.disabled)

  renderError() {
    const reportingTypes = this.getReportingTypes()
    return (
      <div style={styles.container}>{this.renderNoData(reportingTypes)}</div>
    )
  }

  renderSections(foodNDrink, other, healthRecording, onlyHealthCharts) {
    if (onlyHealthCharts) {
      return <div>{this.renderSection(healthRecording)}</div>
    }

    return (
      <div>
        <div style={styles.groupTitle}>Available Charts</div>
        {this.renderSection(foodNDrink)}
        {this.renderSection(healthRecording)}
        {this.renderSection(other)}
      </div>
    )
  }

  renderSuccess(charts) {
    const itemsWithData = _.map(charts, 'key')
    const reportingTypes = this.getReportingTypes()

    const noDataTypes = _.filter(
      reportingTypes,
      (item) => !_.includes(itemsWithData, item.key)
    )

    const other = {
      title: 'Other',
      charts: _.filter(
        charts,
        ({ category }) =>
          category !== 'Health Recordings' && category !== 'Food & Drink'
      ),
      reportingTypes
    }

    const foodNDrink = {
      title: 'Food & Drink',
      charts: _.filter(charts, { category: 'Food & Drink' }),
      reportingTypes
    }

    const healthRecording = {
      title: 'Health Recordings',
      charts: _.filter(charts, { category: 'Health Recordings' }),
      reportingTypes
    }

    // Checks if there are only health charts
    const onlyHealthCharts = !foodNDrink.charts.length && !other.charts.length

    return (
      <div style={styles.container}>
        {this.renderSections(
          foodNDrink,
          other,
          healthRecording,
          onlyHealthCharts
        )}
        {this.renderNoData(noDataTypes)}
      </div>
    )
  }

  render() {
    const {
      router: { params },
      residents,
      testId
    } = this.props
    const selectedResident = _.find(residents, { id: params.id })
    if (!residents || !params.id || !selectedResident) {
      return null
    }

    const pageHeaderTitle = `${selectedResident?.name?.first} ${selectedResident?.name?.last}'s General Charts`

    return (
      <>
        <PageHeader
          sticky
          title={pageHeaderTitle}
          // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
          onBack={
            !isDesktop
              ? () => history.replace?.(`/residents/${params.id}/profile`)
              : undefined
          }
          data-testid={
            testId
              ? `${testId}-charts-dashboard-header`
              : 'charts-dashboard-header'
          }
        />
        <DataSource
          url={`/api/charts/resident/${selectedResident.id}`}
          renderSuccess={(res) => this.renderSuccess(res)}
          renderError={() => this.renderError()}
          testId={testId}
        />
      </>
    )
  }
}

const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/the-care-office/managing-your-residents/how-do-i-view-charts-in-the-care-office'

const styles = {
  container: {
    margin: '0 auto',
    marginBottom: 50,
    marginTop: 24,
    maxWidth: sizes.childScreen
  },
  groupTitle: {
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 16px 24px'
  },
  sectionContainer: {
    marginBottom: 35
  },
  sectionCard: {
    background: colors.white,
    borderRadius: 24,
    padding: 24
  },
  sectionCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 12
  },
  recordCardListItem: {
    background: '#F5F4F7',
    margin: 0,
    padding: '14px 18px'
  }
}

ResidentChartsDashboardScreen.propTypes = {
  logCategories: PropTypes.array,
  logCategoryItems: PropTypes.array,
  residents: PropTypes.array,
  testId: PropTypes.string
}

const mapStateToProps = (state) => ({
  bodyMaps: state.global.home.features?.bodyMaps?.enabled,
  logCategories: state.data['log-categories'],
  logCategoryItems: state.data['log-category-items'],
  residents: state.data.residents,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative
})

export default connect(mapStateToProps)(
  withRouter(ResidentChartsDashboardScreen)
)
